/**
 * Centralized axios client — the ONLY place HTTP leaves the app. Components and
 * hooks call typed service functions that use this client; they never import
 * axios directly.
 *
 * Responsibilities:
 *  - attach the JWT access token and the active-school `X-School` header
 *  - transparently refresh on 401 once, then retry the original request
 *  - on refresh failure, clear tokens and notify the app to log out
 *  - normalize every failure into an {@link ApiError} — callers never see a raw
 *    AxiosError, and raw server text is never surfaced to users
 */
import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import i18n from '@/i18n';
import { schoolContext, SCHOOL_HEADER } from './schoolContext';
import { tokenStorage } from './tokenStorage';
import type { ApiError, SchoolLockedInfo } from './types';

/** Relative to `VITE_API_URL`. dj-rest-auth + SimpleJWT TokenRefreshView. */
const TOKEN_REFRESH_PATH = '/users/auth/token/refresh/';

const baseURL = import.meta.env.VITE_API_URL ?? '/api';

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  // Send cookies so an httpOnly-cookie auth backend works without code changes.
  withCredentials: true,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// ---------------------------------------------------------------------------
// Logout notification — let the app react to an unrecoverable 401 without this
// module importing app/router/store code.
// ---------------------------------------------------------------------------
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler = () => {};

export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler;
}

// ---------------------------------------------------------------------------
// Locked-school notification — the analogous seam for 402 (backend ADR-024).
//
// A school whose subscription lapsed is refused on EVERY ordinary endpoint by
// `billing.middleware.SubscriptionLockMiddleware`, so this cannot be a check in
// one feature: thirteen apps' worth of queries fail at once and each of them
// would otherwise have to recognise the same body. It is deliberately NOT the
// unauthorized path — the credentials are fine, the school owes money, and
// signing the user out would strand them away from the other schools they may
// still have access to.
// ---------------------------------------------------------------------------
/** The backend's machine-readable code. Branch on it, never on the message. */
export const SCHOOL_LOCKED_CODE = 'school_locked';

type SchoolLockedHandler = (info: SchoolLockedInfo) => void;
let onSchoolLocked: SchoolLockedHandler = () => {};

export function setSchoolLockedHandler(handler: SchoolLockedHandler): void {
  onSchoolLocked = handler;
}

// ---------------------------------------------------------------------------
// Permission-denied notification — the third seam, for 403 (backend ADR-029).
//
// Same reasoning as the 402 seam above, one status down: a refusal the server
// has already explained should reach the user in ONE place, not through
// twenty features each recognising the same status. The backend's messages
// name the way out ("Duplicate the test to make it yours"), so the handler's
// whole job is to surface `message` — it never invents copy of its own.
//
// **Only unsafe methods.** A 403 on a GET is a page the feature renders as an
// explanation (`/progress/pupils/:id` is the standing example, FE-ADR-015):
// toasting those would talk over a screen that is already saying it. This
// seam is about a WRITE the user attempted and was refused.
//
// A 400 never reaches it — field errors belong on the form (FE-ADR-013), and
// routing them through a global toast is how a form stops explaining itself.
// ---------------------------------------------------------------------------
type PermissionDeniedHandler = (error: ApiError) => void;
let onPermissionDenied: PermissionDeniedHandler = () => {};

export function setPermissionDeniedHandler(handler: PermissionDeniedHandler): void {
  onPermissionDenied = handler;
}

const SAFE_METHODS = new Set(['get', 'head', 'options']);

function isWrite(config: InternalAxiosRequestConfig | undefined): boolean {
  return !SAFE_METHODS.has((config?.method ?? 'get').toLowerCase());
}

// ---------------------------------------------------------------------------
// Request: attach user auth (JWT) + tenant (school header). The JWT
// authenticates the user; the school header selects the active tenant — both
// are required on school-scoped endpoints.
// ---------------------------------------------------------------------------
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const access = tokenStorage.getAccess();
  if (access) {
    config.headers.set('Authorization', `Bearer ${access}`);
  }

  const schoolSlug = schoolContext.get();
  if (schoolSlug) {
    config.headers.set(SCHOOL_HEADER, schoolSlug);
  }

  // The backend localizes its own messages (validation errors, permission
  // denials) via Django i18n — ask for them in the user's UI language so
  // server-originated copy matches the rest of the screen.
  if (i18n.language) {
    config.headers.set('Accept-Language', i18n.language);
  }

  // File uploads send `FormData`. The instance default `application/json` would
  // make axios serialize it (losing the file) and omit the multipart boundary —
  // drop it so the browser sets `multipart/form-data; boundary=…` itself.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }

  return config;
});

// ---------------------------------------------------------------------------
// Response: single-flight token refresh on 401, then error normalization.
// ---------------------------------------------------------------------------
type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

let refreshPromise: Promise<string> | null = null;

/** Refresh the access token using a bare axios call to avoid interceptor recursion. */
async function refreshAccessToken(): Promise<string> {
  const refresh = tokenStorage.getRefresh();
  if (!refresh) throw new Error('No refresh token');

  // SIMPLE_JWT has ROTATE_REFRESH_TOKENS off → the response carries `access` only.
  const { data } = await axios.post<{ access: string; refresh?: string }>(
    `${baseURL}${TOKEN_REFRESH_PATH}`,
    { refresh },
    { withCredentials: true }
  );
  tokenStorage.set(data.access, data.refresh);
  return data.access;
}

apiClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const isAuthError = error.response?.status === 401;

    if (isAuthError && original && !original._retried && tokenStorage.getRefresh()) {
      original._retried = true;
      try {
        // Coalesce concurrent 401s onto one refresh round-trip.
        refreshPromise ??= refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
        await refreshPromise;
        return apiClient(original);
      } catch {
        tokenStorage.clear();
        onUnauthorized();
      }
    }

    const normalized = normalizeError(error);
    if (normalized.status === 402 && normalized.code === SCHOOL_LOCKED_CODE) {
      onSchoolLocked(lockedInfoFrom(error.response?.data));
    }
    if (normalized.status === 403 && isWrite(original)) {
      // `handled` tells the caller's own error path that this refusal has
      // already been shown — `toastApiError` reads it, so the user gets one
      // message instead of two saying the same thing.
      normalized.handled = true;
      onPermissionDenied(normalized);
    }
    return Promise.reject(normalized);
  }
);

/** The two extra fields `billing.enforcement.locked_payload` carries. */
function lockedInfoFrom(data: unknown): SchoolLockedInfo {
  const body = (data ?? {}) as Record<string, unknown>;
  return {
    subscriptionStatus:
      typeof body.subscription_status === 'string' ? body.subscription_status : null,
    retentionDeadline: typeof body.retention_deadline === 'string' ? body.retention_deadline : null,
  };
}

// ---------------------------------------------------------------------------
// Error normalization.
// ---------------------------------------------------------------------------
function normalizeError(error: AxiosError): ApiError {
  // No response → network error, timeout, or cancellation.
  if (!error.response) {
    const isTimeout = error.code === 'ECONNABORTED';
    return {
      status: 0,
      message: isTimeout
        ? 'The request timed out. Please try again.'
        : 'Network error. Check your connection and try again.',
      raw: error.message,
    };
  }

  const { status, data } = error.response;
  const body = data as Record<string, unknown> | undefined;

  // Osvitly custom handler (common.exceptions): { detail, code } for APIExceptions.
  const detail = typeof body?.detail === 'string' ? body.detail : undefined;

  const fieldErrors: Record<string, string[]> = {};
  const collect = (key: string, value: unknown) => {
    if (Array.isArray(value)) fieldErrors[key] = value.map(String);
    else if (typeof value === 'string') fieldErrors[key] = [value];
  };

  // Osvitly nests serializer field errors under `field_errors`.
  const nested = body?.field_errors;
  if (nested && typeof nested === 'object') {
    for (const [key, value] of Object.entries(nested as Record<string, unknown>))
      collect(key, value);
  }
  // `non_field_errors` is returned at the top level.
  if (Array.isArray(body?.non_field_errors)) collect('non_field_errors', body.non_field_errors);

  // Fallback: a plain DRF response ({ field: [...] }) from any endpoint that
  // bypasses the custom handler (e.g. dj-rest-auth views).
  if (Object.keys(fieldErrors).length === 0 && !detail && body && typeof body === 'object') {
    for (const [key, value] of Object.entries(body)) {
      if (key !== 'code') collect(key, value);
    }
  }

  return {
    status,
    message: detail ?? defaultMessageFor(status),
    // Business codes the SPA branches on (time_expired, submission_graded, …).
    code: typeof body?.code === 'string' ? body.code : undefined,
    fieldErrors: Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined,
    raw: data,
  };
}

function defaultMessageFor(status: number): string {
  if (status === 400 || status === 422) return 'Please check the form for errors.';
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return 'You do not have permission to do that.';
  if (status === 404) return 'The requested item could not be found.';
  if (status >= 500) return 'Something went wrong on our end. Please try again later.';
  return 'Request failed. Please try again.';
}
